import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CustomerService from '../services/customerService';
import { Customer } from '../types';
import AuthService from '../services/authService';

const CustomerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = AuthService.getCurrentUser();
  const userPrefix = currentUser?.role === 'staff' ? '/staff' : '/admin';
  const isEditMode = !!id;

  const [customer, setCustomer] = useState<Customer>({
    phoneNumber: '',
    name: '',
    address: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [showValidationError, setShowValidationError] = useState<boolean>(false);

  useEffect(() => {
    if (isEditMode) {
      loadCustomer();
    }
  }, [id]);

  useEffect(() => {
    if (showValidationError) {
      const timer = setTimeout(() => {
        setShowValidationError(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showValidationError]);

  const loadCustomer = async () => {
    try {
      setLoading(true);
      const response = await CustomerService.getById(Number(id));
      setCustomer(response.data);
      setError(null);
    } catch (err) {
      setError('Không thể tải thông tin khách hàng. Vui lòng thử lại sau.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomer((prev) => ({
      ...prev,
      [name]: value
    }));

    // Xóa lỗi khi người dùng bắt đầu nhập
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!customer.phoneNumber?.trim()) {
      errors.phoneNumber = 'Vui lòng nhập số điện thoại';
    }

    if (!customer.name?.trim()) {
      errors.name = 'Vui lòng nhập tên khách hàng';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = (): boolean => {
    return !!customer.phoneNumber?.trim() && !!customer.name?.trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Luôn hiển thị lỗi khi gửi form không hợp lệ
    if (!validateForm()) {
      setShowValidationError(true);
      // Tạo hiệu ứng rung cho các trường có lỗi
      const invalidInputs = document.querySelectorAll('.is-invalid');
      invalidInputs.forEach(input => {
        input.classList.add('shake-error');
        setTimeout(() => {
          input.classList.remove('shake-error');
        }, 500);
      });
      return;
    }

    try {
      setSubmitting(true);
      if (isEditMode) {
        await CustomerService.update(Number(id), customer);
      } else {
        await CustomerService.create(customer);
      }
      navigate(`${userPrefix}/customers`);
    } catch (err) {
      setError('Có lỗi xảy ra khi lưu thông tin khách hàng. Vui lòng thử lại sau.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="center-screen">
        <div className="spinner-border" role="status" aria-label="loading"></div>
      </div>
    );
  }

  return (
    <div className="customer-form">
      <style>
        {`
          .shake-error {
            animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
          }
          
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
            20%, 40%, 60%, 80% { transform: translateX(5px); }
          }
          
          .input-with-feedback {
            position: relative;
          }
          
          .error-message {
            color: #dc3545;
            font-size: 0.85rem;
            margin-top: 0.25rem;
            display: block;
          }
          
          .form-control.is-invalid {
            border-color: #dc3545;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
            background-repeat: no-repeat;
            background-position: right calc(0.375em + 0.1875rem) center;
            background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
          }
        `}
      </style>

      {/* Title and back button removed — handled by MobileHeader */}

      {error && <div className="alert alert-danger">{error}</div>}

      {showValidationError && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert" style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
          <i className="fas fa-exclamation-circle me-2"></i>
          Vui lòng nhập đầy đủ Số điện thoại và Tên khách hàng
        </div>
      )}

      {Object.keys(fieldErrors).length > 0 && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Vui lòng nhập đầy đủ thông tin các trường bắt buộc được đánh dấu sao (*)
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-12">
            <div className="card h-100" style={{ borderRadius: '10px' }}>
              <div className="card-header bg-primary text-white" style={{ borderRadius: '10px 10px 0 0' }}>
                <i className="fas fa-user me-1"></i>
                {" "}Thông tin khách hàng
              </div>
              <div className="card-body">
                <div className="mb-3 input-with-feedback">
                  <label htmlFor="phoneNumber" className="form-label">Số điện thoại <span className="text-danger">*</span></label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fas fa-phone"></i>
                    </span>
                    <input
                      type="text"
                      className={`form-control ${fieldErrors.phoneNumber ? 'is-invalid' : ''}`}
                      id="phoneNumber"
                      name="phoneNumber"
                      value={customer.phoneNumber || ''}
                      onChange={handleChange}
                      required
                      placeholder="Nhập số điện thoại khách hàng"
                    />
                  </div>
                  {fieldErrors.phoneNumber && (
                    <div className="error-message">
                      <i className="fas fa-exclamation-circle me-1"></i>
                      {fieldErrors.phoneNumber}
                    </div>
                  )}
                </div>

                <div className="mb-3 input-with-feedback">
                  <label htmlFor="name" className="form-label">Tên khách hàng <span className="text-danger">*</span></label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fas fa-user"></i>
                    </span>
                    <input
                      type="text"
                      className={`form-control ${fieldErrors.name ? 'is-invalid' : ''}`}
                      id="name"
                      name="name"
                      value={customer.name || ''}
                      onChange={handleChange}
                      required
                      placeholder="Nhập tên khách hàng"
                    />
                  </div>
                  {fieldErrors.name && (
                    <div className="error-message">
                      <i className="fas fa-exclamation-circle me-1"></i>
                      {fieldErrors.name}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="address" className="form-label">Địa chỉ</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fas fa-map-marker-alt"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      id="address"
                      name="address"
                      value={customer.address || ''}
                      onChange={handleChange}
                      placeholder="Nhập địa chỉ khách hàng (không bắt buộc)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-12">
            <div className="d-flex gap-2 justify-content-end" style={{ gap: '0.5rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={submitting}
                onClick={() => navigate(`${userPrefix}/customers`)}
                style={{ fontSize: '1rem' }}
              >
                <i className="fas fa-close me-1"></i> Hủy
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
                style={{ fontSize: '1rem' }}
                onClick={(e) => {
                  if (!isFormValid()) {
                    e.preventDefault();
                    setShowValidationError(true);
                  }
                }}
              >
                {submitting ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <>
                    <i className="fas fa-save me-1"></i> {isEditMode ? 'Cập nhật' : 'Thêm mới'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CustomerForm; 